import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import formatter from '../utils/formatter';
import BarChart from 'recharts/es6/chart/BarChart';
import Bar from 'recharts/es6/cartesian/Bar';
import XAxis from 'recharts/es6/cartesian/XAxis';
import YAxis from 'recharts/es6/cartesian/YAxis';
import CartesianGrid from 'recharts/es6/cartesian/CartesianGrid';
import ReferenceLine from 'recharts/es6/cartesian/ReferenceLine';
import ResponsiveContainer from 'recharts/es6/component/ResponsiveContainer';
import Tooltip from 'recharts/es6/component/Tooltip';
import Legend from 'recharts/es6/component/Legend';
import VerticalBarLabel from './VerticalBarLabel';
import { computed } from 'mobx';
import { roundNumber } from '@kurtosys/ksys-app-template/dist/utils/number';
import { TickHandler } from '../utils/TickHandler';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { ChartConfig } from '../models/configuration/chart/ChartConfig';
import { DataStore } from '../stores/DataStore';
import { Series } from '../models/configuration/chart/Series';
import { DataSetValue } from '../models/components/ChartStore/DataSetValue';
import { Theme } from '../models/Theme';
const ResponsiveChartContainer = styled<any, any>(
    ResponsiveContainer,
    'responsiveChartContainer',
    'chartBarChart'
)`
    ${propStyles};
`;

ResponsiveChartContainer.defaultProps = {
    fontSize: 'xs',
    minHeight: '200px',
};

interface ChartBarChartProps {
    config?: Partial<ChartConfig>;
    theme?: Theme;
    dataStore?: Partial<DataStore>;
    data?: Array<DataSetValue>;
    xAxis?: XAxis;
    yAxis?: YAxis;
    layout?: string;
    minHeight?: number;
    aspect?: number;
    getThemeColor?: (color: string) => void;
    getLabelName?: (label: any) => { value: string };
    getSeriesName?: (series: Series) => string;
    getBaseFormatOptions?: (
        formatOptions: any,
        v?: any
    ) => {
        dataStore: DataStore;
        locale: string;
        format: any;
        type: any;
        currency: string;
        options: any;
        value: any;
    };
    isMobile?: boolean;
    renderAccessibleTable?: (
        data: Array<object>,
        columns?: Array<{
            label: string;
            dataKey: string;
            format?: any;
        }>
    ) => JSX.Element;
    mobileConfig?: Partial<ChartConfig>;
    series?: Array<Series>;
    showBlankSeries?: boolean;
}

@inject('dataStore')
@observer
class ChartBarChart extends Component<ChartBarChartProps, any> {
    private yAxisId = 'left';

    @computed
    get config() {
        const { isMobile, config, mobileConfig } = this.props;
        if (isMobile) {
            return { ...config, ...mobileConfig };
        }
        return config;
    }

    @computed
    get barChartMobileProps() {
        const { isMobile } = this.props;
        const { config } = this;
        const { barChart } = config;
        if (isMobile && barChart) {
            return { ...barChart };
        }
    }

    @computed
    get maxBarSize() {
        const { isMobile } = this.props;
        const { config } = this;
        const { barChart } = config;

        if (!isMobile && barChart && barChart.maxBarSize) {
            return { maxBarSize: barChart.maxBarSize };
        }
    }

    @computed
    get referenceLines() {
        const { isMobile } = this.props;
        const { config } = this;
        const { barChart } = config;

        if (
            barChart &&
            barChart.referenceLines &&
            barChart.referenceLines.length > 0
        ) {
            const referenceLineComponents: Array<JSX.Element> = [];
            barChart.referenceLines.forEach(referenceLine => {
                const y = referenceLine.y || 0;
                // Flip reference lines axis for vertical bar chart in mobile view
                const axis = isMobile ? { x: y } : { y };
                const referenceLineComponent = (
                    <ReferenceLine
                        key={referenceLine.y}
                        yAxisId={this.yAxisId}
                        stroke={referenceLine.stroke || 'black'}
                        strokeWidth={referenceLine.strokeWidth || 1}
                        {...axis}
                    />
                );
                referenceLineComponents.push(referenceLineComponent);
            });

            return referenceLineComponents;
        }
    }

    seriesHasData(key: string) {
        const { data } = this.props;
        if (!isNullOrEmpty(data)) {
            if (Array.isArray(data)) {
                return (
                    data.filter(item => !isNullOrEmpty(item[key])).length > 0
                );
            }
            return !isNullOrEmpty(data[key]);
        }
        return false;
    }

    @computed
    get series() {
        const { series, showBlankSeries = false } = this.props;
        if (
            !showBlankSeries &&
            !isNullOrEmpty(series) &&
            Array.isArray(series)
        ) {
            return series.filter(s => this.seriesHasData(s.dataKey));
        }
        return series;
    }

    @computed
    get yDomain() {
        const { yAxis, series, data } = this.props;
        if (yAxis && yAxis.domain) {
            return yAxis.domain;
        }
        const values = [];
        // Get values from each series data key for min and max
        series.forEach(element => {
            data.map(row => {
                const value = row[element.dataKey];
                if (value) {
                    values.push(value);
                }
            });
        });

        let min = Math.min(...values);
        let max = Math.max(...values);

        // Calculate if min & max range contains decimals
        const absMin = Math.abs(min);
        const absMax = Math.abs(max);
        const isMinDecimal = absMin < 1 && absMin > 0;
        const isMaxDecimal = absMax < 1 && absMax > 0;
        const isDecimalRange = isMinDecimal || isMaxDecimal;

        // Update min max range and multiply based on the smallest decimal.
        let multiple = 0;
        if (isDecimalRange) {
            multiple = this.getAbsDecimalMultiple(
                absMin < absMax ? absMin : absMax
            );
            min *= multiple;
            max *= multiple;
        }

        /**
         * if both min & max are above or below zero, set zero as a domain on the appropriate side.
         * otherwise,
         * round the integer down/up based on the what side of the domain you are on.
         */
        let minDomain = min < 0 ? Math.floor(min) : 0;
        let maxDomain = max < 0 ? 0 : Math.ceil(max);

        // Divide integer back down to a decimal, post integer rounding, if the original data were decimals.
        if (isDecimalRange) {
            minDomain /= multiple;
            maxDomain /= multiple;
        }

        return [minDomain, maxDomain];
    }

    getAbsDecimalMultiple(absoluteDecimal: number): number {
        let multiple = 10;
        const decimalPoints = [0.1, 0.01, 0.001, 0.0001, 0.00001];
        for (const decimalPoint of decimalPoints) {
            if (absoluteDecimal >= decimalPoint) {
                return multiple;
            }
            multiple *= 10;
        }
    }

    @computed
    get seriesDataKeys() {
        const { series } = this;
        if (Array.isArray(series)) {
            return series.map(item => item.dataKey);
        }
        return [];
    }

    applyTickPivotPoint(element: XAxis | YAxis) {
        const { tickPivotPoint } = element;
        if (typeof tickPivotPoint === 'number') {
            const { data } = this.props;
            const tickHandler = new TickHandler();
            element.ticks = tickHandler.getTicksForSeries(
                data,
                this.seriesDataKeys,
                tickPivotPoint
            );
        }
    }

    render() {
        const {
            theme,
            dataStore,
            data,
            xAxis,
            yAxis,
            layout,
            minHeight,
            aspect,
            getThemeColor,
            getLabelName,
            getSeriesName,
            getBaseFormatOptions,
            isMobile,
            renderAccessibleTable,
        } = this.props;
        const {
            barChartMobileProps,
            maxBarSize,
            yAxisId,
            referenceLines,
            yDomain,
            config,
        } = this;

        const { label: labelConfig } = config;
        const labelPrecision = labelConfig && labelConfig.precision;
        let xAxisLeftPadding = 10;

        if (isMobile && labelConfig) {
            let maxLength = 0;

            for (const item of data.filter(d => d.value < 0)) {
                let { value } = item;
                if (labelPrecision) {
                    value = roundNumber(value as number, labelPrecision, false);
                }
                const length = `${value}`.length;
                maxLength = Math.max(maxLength, length);
            }

            if (maxLength > 0) {
                xAxisLeftPadding += maxLength * labelConfig.spacing;
            }
        }

        const xAxisPadding = xAxis.padding || {
            left: xAxisLeftPadding,
            right: xAxisLeftPadding,
        };

        this.applyTickPivotPoint(xAxis);
        this.applyTickPivotPoint(yAxis);

        return (
            <React.Fragment>
                {renderAccessibleTable &&
                    renderAccessibleTable(data, [
                        {
                            label:
                                layout === 'vertical'
                                    ? getLabelName(yAxis.label).value
                                    : getLabelName(xAxis.label).value,
                            dataKey:
                                layout === 'vertical'
                                    ? yAxis.dataKey
                                    : xAxis.dataKey,
                            format: config.tooltip.labelFormat
                                ? getBaseFormatOptions(
                                      config.tooltip.labelFormat
                                  )
                                : null,
                        },
                        ...this.series.map(s => {
                            return {
                                label: getSeriesName(s),
                                dataKey: s.dataKey,
                                format: config.tooltip.valueFormat
                                    ? getBaseFormatOptions(
                                          config.tooltip.valueFormat
                                      )
                                    : null,
                            };
                        }),
                    ])}
                <div aria-hidden="true">
                    <ResponsiveChartContainer
                        aspect={aspect}
                        minHeight={minHeight}
                        {...theme.ResponsiveChartContainer}
                    >
                        <BarChart
                            data={data}
                            layout={layout}
                            margin={
                                layout === 'vertical'
                                    ? { top: 5, right: 30, bottom: 5, left: 0 }
                                    : {}
                            }
                            {...barChartMobileProps}
                            {...maxBarSize}
                        >
                            {config.grid && (
                                <CartesianGrid
                                    {...config.grid}
                                    vertical={
                                        isMobile
                                            ? config.grid.horizontal
                                            : config.grid.vertical
                                    }
                                    horizontal={
                                        isMobile
                                            ? config.grid.vertical
                                            : config.grid.horizontal
                                    }
                                    stroke={getThemeColor(config.grid.stroke)}
                                    fill={getThemeColor(config.grid.fill)}
                                />
                            )}
                            {!isMobile && config.tooltip && (
                                <Tooltip
                                    cursor={false}
                                    {...config.tooltip}
                                    labelFormatter={v =>
                                        config.tooltip.labelFormat
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      config.tooltip
                                                          .labelFormat,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                    formatter={v =>
                                        config.tooltip.valueFormat
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      config.tooltip
                                                          .valueFormat,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                />
                            )}
                            {config.legend && (
                                <Legend
                                    iconType="circle"
                                    wrapperStyle={{ bottom: 0 }}
                                    // TODO: validate the interface used here as only objects and arrays can be spread
                                    {...(config.legend as any)}
                                />
                            )}
                            {xAxis && (
                                <XAxis
                                    dataKey={xAxis.dataKey}
                                    tickLine={false}
                                    padding={xAxisPadding}
                                    {...xAxis}
                                    label={{
                                        ...getLabelName(xAxis.label),
                                        ...{
                                            offset: 0,
                                            angle: 0,
                                            position: 'insideBottom',
                                        },
                                    }}
                                    tickFormatter={v =>
                                        xAxis.format
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      xAxis.format,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                    domain={(isMobile && yDomain) || undefined}
                                />
                            )}
                            {yAxis && (
                                <YAxis
                                    dataKey={yAxis.dataKey}
                                    yAxisId={yAxisId}
                                    orientation="left"
                                    axisLine={false}
                                    tickLine={false}
                                    {...yAxis}
                                    label={getLabelName(yAxis.label)}
                                    tickFormatter={v =>
                                        yAxis.format
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      yAxis.format,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                    domain={!isMobile && yDomain}
                                />
                            )}
                            {this.series.map((s, index) => (
                                <Bar
                                    key={`bar-${s.dataKey}-${index}`}
                                    dataKey={s.dataKey}
                                    yAxisId={yAxisId}
                                    {...s}
                                    name={getSeriesName(s)}
                                    label={
                                        isMobile ? (
                                            <VerticalBarLabel
                                                // TODO: validate the interface used here as only objects and arrays can be spread
                                                {...(s.label as any)}
                                                precision={labelPrecision}
                                            />
                                        ) : (
                                            false
                                        )
                                    }
                                    fill={getThemeColor(s.fill)}
                                    isAnimationActive={!dataStore.isExpandAll}
                                />
                            ))}
                            {...referenceLines}
                        </BarChart>
                    </ResponsiveChartContainer>
                </div>
            </React.Fragment>
        );
    }
}

export default withTheme(ChartBarChart) as any;



// WEBPACK FOOTER //
// ./src/Components/ChartBarChart.tsx